import { useEffect } from "react";
import Header from "../Header";
import PageTransition from "../../PageTransition";
import "./BlogDetails.css";


import img1 from "./intelImages/best-iphone-camera-settings.webp";
import img2 from "./intelImages/lightning-composition-tips.webp";
import img3 from "./intelImages/editing-photos-on-iphone.webp";

export default function BlogDetailsIphonePhotography() {
  useEffect(() => {
    document.body.style.overflow = "auto";
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header visible mode="normal" />

      <PageTransition>
        <section className="blog-detail">
          <div className="blog-container container">

            {/* ================= HERO ================= */}
            <header className="blog-hero">
              <h1>Point, Shoot, and Wow: How to Make Your Photography Better on iPhone</h1>

              <p className="blog-subtext">
                You don’t need a bulky DSLR to capture gallery-worthy images. The most
                powerful camera in the world is the one already in your pocket.
              </p>

              <p className="blog-subtext">
                However, there is a massive difference between a "snapshot" and a
                professional photograph.
              </p>

              <p className="blog-subtext">
                If you’re ready to stop taking blurry photos and start creating art,
                here is how to master iPhone photography in five simple steps.
              </p>
            </header>

            {/* ================= SECTION ================= */}
            <section className="blog-section">
              <h2>Why iPhone is Great for Photography</h2>
              <p>
                The iPhone isn't just a phone; it’s a sophisticated computational
                engine. Between Deep Fusion technology and Night Mode, the hardware
                does the heavy lifting, allowing you to focus entirely on the creative
                side.
              </p>
              <p>
                Its portability means you’re always ready to capture a "decisive
                moment" that a traditional photographer might miss while setting up
                a tripod.
              </p>
            </section>

            {/* ================= SPLIT 1 ================= */}
            <section className="blog-split blog-split2">
              <div className="audit-list">
                <h2>Best iPhone Camera Settings</h2>

                <p>
                  Before you hit the shutter, you need to calibrate your gear. Small
                  tweaks in your best camera settings for iPhone photography can lead
                  to massive jumps in quality:
                </p>

                <p><strong>Grid Lines:</strong> Go to Settings &gt; Camera &gt; Grid.
                  Turning this on helps you follow the Rule of Thirds.</p>

                <p><strong>Macro Control:</strong> If you have a Pro model, ensure
                  "Macro Control" is toggled on for stunning close-ups of textures
                  and nature.</p>

                <p><strong>ProRAW:</strong> For those who want to take professional
                  photos on iPhone, turn on Apple ProRAW. It captures more data,
                  giving you much more control during the editing process.</p>

                <p><strong>Turn off the Flash:</strong> Built-in mobile flashes are
                  usually harsh. Trust the iPhone’s "Night Mode" instead—it produces
                  much more natural light.</p>
              </div>

              <div className="image-box">
                <img src={img1} alt="iPhone camera settings example" />
              </div>
            </section>

            {/* ================= SPLIT 2 ================= */}
            <section className="blog-split">
              <div className="audit-list">
                <h2>Lighting & Composition Tips</h2>

                <p><strong>Find the "Golden Hour":</strong> The hour just after sunrise
                  or before sunset provides soft, warm light that makes everything
                  look cinematic.</p>

                <p><strong>Leading Lines:</strong> Use roads, fences, or shadows to
                  lead the viewer’s eye toward your subject.</p>

                <p><strong>Lock Your Focus:</strong> Tap your subject on the screen
                  and hold until you see "AE/AF Lock."</p>

                <p><strong>Adjust Exposure Manually:</strong> After tapping to focus,
                  swipe your finger up or down on the sun icon to manually brighten
                  or darken the shot.</p>
              </div>

              <div className="image-box">
                <img src={img2} alt="iPhone photography composition example" />
              </div>
            </section>

            {/* ================= SECTION ================= */}
            <section className="blog-section">
              <h2>Editing Photos on iPhone</h2>

              <p>
                The photo isn't finished when you click the button—it's finished in
                the edit. You don't need a computer to improve mobile photography;
                you just need the right workflow:
              </p>

              <p>
                <strong>The Native Photos App:</strong> Apple’s "Auto" enhance is
                good, but manually adjusting the Brilliance and Black Point sliders
                adds instant depth.
              </p>

              <p>
                <strong>Third-Party Power:</strong> Use apps like Lightroom Mobile or
                Snapseed for selective editing (changing the sky without affecting
                the ground).
              </p>

              <p>
                <strong>Don't Over-Filter:</strong> Avoid heavy, pre-set filters.
                Instead, aim for subtle "color grading" that enhances the natural
                mood of the shot.
              </p>
            </section>

            {/* ================= WIDE IMAGE ================= */}
            <div className="image-wide">
              <img src={img3} alt="Edited iPhone photo cinematic look" />
            </div>

            {/* ================= FINAL ================= */}
            <section className="blog-section">
              <h2>Common iPhone Photography Mistakes</h2>

              <p><strong>Dirty Lens:</strong> This is the #1 cause of "hazy" photos.
                Wipe your lens with a microfiber cloth before every shoot.</p>

              <p><strong>Digital Zoom:</strong> Never "pinch to zoom." It destroys
                image quality. Instead, walk closer to your subject or use the
                dedicated 2x or 3x telephoto lenses.</p>

              <p><strong>Centering Everything:</strong> Placing your subject right
                in the middle can feel static. Try placing them to the left or
                right to create more visual energy.</p>
            </section>

            <section className="blog-section">
              <h2>The Final Shot</h2>
              <p>
                Mastering iPhone photography tips for beginners is about moving from
                "taking" a photo to "making" a photo. Clean your lens, lock your
                focus, and start seeing the world through a cinematic frame.
              </p>

              <p>
                Want to see these tips in action? Follow us for our weekly
                "Shot on iPhone" breakdown!
              </p>
            </section>

          </div>
        </section>
      </PageTransition>
    </>
  );
}
